import { createContext, useContext, useEffect, useRef } from 'react';
import * as RouterDom from 'react-router-dom';

/**
 * Leave guard for a dirty editor (C1 §1.1: the prototype silently kept the
 * editor across navigation and lost Write-mode edits; the rebuild guards).
 *
 * - Closing / reloading the tab: the browser's own "Leave site?" prompt
 *   (`beforeunload`).
 * - In-app navigation (sidebar, crumbs, TopBar, links): the app runs a plain
 *   <BrowserRouter>, where react-router's `useBlocker` is unavailable, so the
 *   router's navigator `push`/`replace` are intercepted while `when` is true.
 *   A blocked navigation is handed to `onBlock(proceed)`; the editor shows its
 *   own "Discard your changes?" row and calls `proceed()` on Discard.
 * - Browser Back/Forward (popstate) cannot be held by a BrowserRouter and is
 *   not intercepted (documented gap).
 *
 * `allowRef.current = true` lets the editor's own exits (Cancel, Save, Publish)
 * through without asking.
 */

const NoNavigation = createContext(null);
// The test router mock has no navigation context; fall back to nothing.
const NavigationContext = RouterDom.UNSAFE_NavigationContext || NoNavigation;

export default function useLeaveGuard({ when, onBlock, allowRef }) {
    const nav = useContext(NavigationContext);
    const navigator = nav && nav.navigator;
    const onBlockRef = useRef(onBlock);
    useEffect(() => {
        onBlockRef.current = onBlock;
    });

    useEffect(() => {
        if (!when) return undefined;
        const onBeforeUnload = (event) => {
            if (allowRef && allowRef.current) return undefined;
            event.preventDefault();
            // Chrome needs a returnValue to show the prompt.
            event.returnValue = '';
            return '';
        };
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }, [when, allowRef]);

    useEffect(() => {
        if (!when || !navigator || typeof navigator.push !== 'function') return undefined;
        const { push, replace } = navigator;
        const guard = (original) => (...args) => {
            if (allowRef && allowRef.current) return original.apply(navigator, args);
            const proceed = () => {
                if (allowRef) allowRef.current = true;
                return original.apply(navigator, args);
            };
            if (typeof onBlockRef.current === 'function') onBlockRef.current(proceed);
            return undefined;
        };
        navigator.push = guard(push);
        if (typeof replace === 'function') navigator.replace = guard(replace);
        return () => {
            navigator.push = push;
            if (typeof replace === 'function') navigator.replace = replace;
        };
    }, [when, navigator, allowRef]);
}
